import firebase from 'firebase/app';
import 'firebase/functions';
import 'firebase/firestore';
import adapter from 'webrtc-adapter';

adapter;

const webRTCConfigs = {
  bundlePolicy: 'max-bundle',
  iceServers: [
    { urls: 'stun:stun.l.google.com:19302' },
    {
      urls: ['turn:turn.robotmon.app?transport=udp', 'turn:turn.robotmon.app?transport=tcp'],
      credential: 'robotmon-98370',
      username: 'xrobotmon',
    },
  ],
};

function validateIPaddress(ipaddress) {
  if (
    /^(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/.test(
      ipaddress
    )
  ) {
    return true;
  }
  return false;
}

class WebRTC {
  constructor(licenseId, deviceId, supportGame) {
    this.licenseId = licenseId;
    this.deviceId = deviceId;
    this.timeout = 15000;
    this.userDisconnect = false;

    this.postConnectionCall = firebase.functions().httpsCallable('xPostConnection');
    this.connection = undefined;
    this.sendChannel = undefined;
    this.onConnectedCallback = undefined;
    this.onDisconnectedCallback = undefined;
    this.trackEvent = undefined;
    this.offer = '';
    this.answer = '';
    this.unsubscribe = undefined;
    this.unsubscribeWebRTCInfo = undefined;
    this.isConnected = false;
    this.timeoutFuncId = undefined;
    this.retryCount = 0;
    this.supportGame = supportGame;
  }

  static _isCompatible() {
    function iOSversion() {
      if (/iP(hone|od|ad)/.test(navigator.platform)) {
        var v = navigator.appVersion.match(/OS (\d+)_(\d+)_?(\d+)?/);
        return [parseInt(v[1], 10), parseInt(v[2], 10), parseInt(v[3] || 0, 10)];
      }
    }
    const ver = iOSversion();
    if (ver !== undefined) {
      if (ver[0] <= 12) {
        if (ver[1] < 2) {
          return false;
        }
      }
      return true;
    }
    return true;
  }

  async updateWebRTCInfoSession(remoteSession) {
    try {
      await firebase
        .firestore()
        .collection('x_webrtc')
        .doc('device_' + this.deviceId)
        .update({
          supportGame: this.supportGame,
          deviceId: 'device_' + this.deviceId,
          webRTCLocalSession: '',
          webRTCRemoteSession: remoteSession,
          webRTCUpdateTime: Math.floor(Date.now() / 1000),
        });
    } catch (e) {
      console.log('updateWebRTCInfoSessionError', e);
    }
  }

  connect() {
    this.retryCount = 0;
    this._connect();
  }

  _connect() {
    console.log('Try to connect to device', this.retryCount);

    // === start webRTC connection === //
    this.connection = new RTCPeerConnection(webRTCConfigs);

    // onTrack
    this.connection.ontrack = event => {
      console.log('ontrack', event);
      this.trackEvent = event;
    };

    // create Data channel
    this.sendChannel = this.connection.createDataChannel('robotmonChannel');

    this.connection.oniceconnectionstatechange = e => {
      if (this.connection === undefined) {
        return;
      }
      console.log('onIceConnectinChange: ', this.connection.iceConnectionState, e);
      //checking
      if (this.connection.iceConnectionState === 'checking') {
        // DO NOTHING
      } else if (this.connection.iceConnectionState === 'connected') {
        this.isConnected = true;
        this.userDisconnect = true;
        console.log(this.connection.remoteDescription.sdp);
        if (this.onConnectedCallback !== undefined) {
          this.onConnectedCallback(this.trackEvent);
        }
      } else if (this.connection.iceConnectionState === 'disconnected') {
        if (this.userDisconnect) {
          this._disconnect();
          console.log('webrtc user disconnected or network unstable');
        } else {
          this.retry();
        }
      } else if (this.connection.iceConnectionState === 'failed') {
        this.retry();
      }
    };

    const candidates = [];
    this.connection.onicecandidate = event => {
      if (event.candidate !== null) {
        candidates.push(event.candidate.candidate);
      } else if (event.candidate === null) {
        const offer = {
          type: 'offer',
          sdp: this.filterOutSdp(this.connection.localDescription.sdp),
        };
        console.log(offer.sdp);
        this.offer = btoa(JSON.stringify(offer));
        this.waitAnswer();
        this.updateWebRTCInfoSession(this.offer)
          .then(() => {
            console.log('Offer2 sent success.');
          })
          .catch(e => {
            console.log('postConnectionCall2 Failed', e);
          });
        this.postConnectionCall({
          deviceId: this.deviceId,
          remoteSession: this.offer,
          candidates,
          connectType: 'stun',
        })
          .then(() => {
            console.log('Offer sent success.');
          })
          .catch(e => {
            console.log('postConnectionCall Failed', e);
          });
      }
    };

    this.connection.addTransceiver('video', { direction: 'recvonly' });
    // creat offer
    this.connection
      .createOffer()
      .then(d => this.connection.setLocalDescription(d))
      .catch(console.log);
  }

  retry() {
    console.log(`===== RETRY ===== ${this.retryCount}`);
    if (this.retryCount < 5) {
      this.retryCount++;
      this._disconnect();
      this._connect();
    } else {
      this.userDisconnect = true; // really disconnect
      this._disconnect();
    }
  }

  _disconnect() {
    this.sendMessage('disconnect');
    if (this.connection !== undefined) {
      this.connection.close();
    }
    if (this.unsubscribe !== undefined) {
      this.unsubscribe();
    }
    if (this.sendChannel !== undefined) {
      this.sendChannel.close();
    }
    this.connection = undefined;
    this.sendChannel = undefined;
    this.offer = '';
    this.answer = '';
    this.unsubscribe = undefined;
    this.trackEvent = undefined;
    this.isConnected = false;
    if (this.timeoutFuncId !== undefined) {
      clearTimeout(this.timeoutFuncId);
      this.timeoutFuncId = undefined;
    }
    if (this.userDisconnect && this.onDisconnectedCallback !== undefined) {
      this.onDisconnectedCallback();
    }
  }

  disconnect() {
    this.userDisconnect = true;
    this._disconnect();
  }

  onConnected(cb) {
    this.onConnectedCallback = cb;
  }

  onDisconnected(cb) {
    this.onDisconnectedCallback = cb;
  }

  waitAnswer() {
    if (this.offer === '' || this.answer !== '' || this.unsubscribe !== undefined) {
      return;
    }
    console.log('Wait for Answer...');
    this.timeoutFuncId = setTimeout(() => {
      this.onTimeout();
    }, this.timeout);

    console.log('unsubscribeWebRTCInfo', 'device_' + this.deviceId);
    this.unsubscribeWebRTCInfo = firebase
      .firestore()
      .collection('x_webrtc')
      .doc('device_' + this.deviceId)
      .onSnapshot(doc => {
        const info = doc.data();
        if (info === undefined) {
          return;
        }
        if (info.webRTCLocalSession !== '' && info.webRTCRemoteSession === this.offer) {
          this.answer = info.webRTCLocalSession;
          this.unsubscribeWebRTCInfo();
          this.unsubscribe();
          this.unsubscribe = undefined;
          this.unsubscribeWebRTCInfo = undefined;
          console.log('=== start session ===', {
            answer: this.answer,
            offer: this.offer,
          });
          const answerObject = JSON.parse(atob(this.answer));
          answerObject.sdp = this.filterOutSdp(answerObject.sdp);
          console.log(answerObject.sdp);
          try {
            this.connection.setRemoteDescription(new RTCSessionDescription(answerObject));
          } catch (e) {
            console.log(e);
          }
        }
      });

    this.unsubscribe = firebase
      .firestore()
      .collection('x_devices')
      .doc(this.deviceId)
      .onSnapshot(doc => {
        const xDevice = doc.data();
        if (xDevice === undefined) {
          return;
        }
        // console.log("onSnapshot", xDevice.webRTCLocalSession !== "", xDevice.webRTCRemoteSession === this.offer);
        if (xDevice.webRTCLocalSession !== '' && xDevice.webRTCRemoteSession === this.offer) {
          this.answer = xDevice.webRTCLocalSession;
          this.unsubscribe();
          this.unsubscribeWebRTCInfo();
          this.unsubscribe = undefined;
          this.unsubscribeWebRTCInfo = undefined;
          console.log('=== start session ===', {
            answer: this.answer,
            offer: this.offer,
          });
          const answerObject = JSON.parse(atob(this.answer));
          answerObject.sdp = this.filterOutSdp(answerObject.sdp);
          console.log(answerObject.sdp);
          try {
            this.connection.setRemoteDescription(new RTCSessionDescription(answerObject));
          } catch (e) {
            console.log(e);
          }
        }
      });
  }

  filterOutSdp(sdp) {
    const copyLines = [];
    const lines = sdp.split('\n');
    for (const line of lines) {
      if (line.indexOf('candidate:foundation') !== -1 && line.indexOf('172.') !== -1) {
        continue;
      }
      if (line.indexOf('candidate:') !== -1 && line.indexOf('network-cost') !== -1) {
        let fields = line.split(' ');
        if (fields[4] !== undefined) {
          if (!validateIPaddress(fields[4])) {
            continue;
          }
        }
      }
      copyLines.push(line);
    }
    return copyLines.join('\n');
  }

  onTimeout() {
    if (!this.isConnected) {
      console.log('timeout, retry...');
      this.retry();
    }
  }

  sendMessage(msg) {
    if (this.sendChannel === undefined) {
      return false;
    }
    if (this.sendChannel.readyState === 'open') {
      this.sendChannel.send(msg);
      return true;
    }
    return false;
  }
}
WebRTC.isCompatible = WebRTC._isCompatible();

export default WebRTC;
